import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import PageWrapper from '@/components/feature/PageWrapper';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, movies, categories } from '@/mocks/data';
import { movieArticleMap } from '@/components/feature/MovieCard';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background-50">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-3">文章未找到</h2>
          <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors cursor-pointer text-sm">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.categorySlug);
  const relatedMovie = movies.find((m) => m.id === article.relatedMovieId);
  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
    .slice(0, 3);

  const heroImage = article.coverUrl;

  return (
    <>
      <SEOHead
        title={`${article.title} | 韩影深解`}
        description={article.excerpt}
        keywords={`${article.title},韩国电影,韩国电影推荐,韩国电影解析,韩国电影影评`}
        canonicalUrl={`https://hanyingshenjie.com/article/${article.slug}`}
        ogType="article"
        ogImage={heroImage}
        articleData={{
          publishedTime: article.date,
          modifiedTime: article.date,
          author: article.author,
          section: category?.name,
        }}
      />
      <main>
        <PageWrapper
          heroImage={heroImage}
          title={article.title}
          subtitle={`作者：${article.author} · ${article.date}`}
        />

        <div className="w-full bg-background-100 border-b border-background-300/20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '深度解析', href: '/article/bong-joon-ho-narrative-evolution' },
                ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-wrap items-center gap-2 mb-8">
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="px-3 py-1 bg-primary-100/70 text-primary-700 text-xs font-medium rounded-full hover:bg-primary-200/70 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {category.name}
                </Link>
              )}
              <span className="text-xs text-foreground-400">{article.author}</span>
              <span className="text-xs text-foreground-400">·</span>
              <span className="text-xs text-foreground-400">{article.date}</span>
            </div>

            <div
              className="prose prose-invert max-w-none text-foreground-700 leading-relaxed text-sm md:text-base"
              dangerouslySetInnerHTML={{
                __html: article.content
                  .replace(/<h3>/g, '<h3 class="font-heading text-lg md:text-xl font-semibold text-foreground-900 mt-8 mb-4">')
                  .replace(/<p>/g, '<p class="mb-4 leading-relaxed">'),
              }}
            />

            {relatedMovie && (
              <div className="mt-12 p-5 md:p-6 bg-background-100 rounded-lg border border-background-300/30">
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">
                  相关电影
                </h3>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to={`/article/${movieArticleMap[relatedMovie.id] || 'bong-joon-ho-narrative-evolution'}`}
                    className="flex-shrink-0 w-24 md:w-28 cursor-pointer"
                  >
                    <div className="aspect-[2/3] rounded-md overflow-hidden bg-background-200">
                      <img
                        src={relatedMovie.posterUrl}
                        alt={relatedMovie.title}
                        className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                  </Link>
                  <div className="flex-1">
                    <Link
                      to={`/article/${movieArticleMap[relatedMovie.id] || 'bong-joon-ho-narrative-evolution'}`}
                      className="font-heading text-base font-semibold text-foreground-900 hover:text-primary-500 transition-colors cursor-pointer"
                    >
                      {relatedMovie.title}
                    </Link>
                    <p className="text-xs text-foreground-500 mt-1">
                      {relatedMovie.director} · {relatedMovie.year} · {relatedMovie.runtime}
                    </p>
                    <p className="text-sm text-foreground-600 mt-3 leading-relaxed">
                      {relatedMovie.summary}
                    </p>
                    <p className="text-xs text-foreground-400 mt-2">
                      主演：{relatedMovie.cast.join('、')}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard
                    key={ra.id}
                    id={ra.id}
                    title={ra.title}
                    slug={ra.slug}
                    excerpt={ra.excerpt}
                    author={ra.author}
                    date={ra.date}
                    coverUrl={ra.coverUrl}
                    categorySlug={ra.categorySlug}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}